/**
 * @file LayoutService.ts
 * @role The single source of layout truth. Computes a LayoutContext from viewport
 *       size and publishes layout snapshots. All responsive UI flows through here;
 *       components never read window size or check orientation themselves.
 * @owns Current LayoutContext, direct subscribers, resize/orientation/DPR listeners.
 * @reads An injected ViewportProbe and emits orientation notifications through EventBus.
 * @neverDoes Position individual UI elements (they implement IResponsiveElement).
 * @invariants 14 (no scattered orientation checks).
 * @see docs/UI_LAYOUT_GUIDE.md
 */
import type { EventBus } from '@core/events/EventBus';
import { orientationForSize } from './Orientation';
import type {
  HeightClass,
  LayoutBounds,
  LayoutContext,
  WidthClass,
} from './LayoutContext';
import { ZERO_SAFE_AREA, type SafeArea } from './SafeArea';
import {
  BrowserViewportProbe,
  type ViewportMeasurement,
  type ViewportProbe,
} from './ViewportProbe';

export interface LayoutServiceOptions {
  safeAreaProvider?: () => SafeArea;
  viewportProbe?: ViewportProbe;
}

export type LayoutListener = (context: LayoutContext) => void;

export class LayoutService {
  private readonly viewportProbe: ViewportProbe;
  private readonly listeners = new Set<LayoutListener>();
  private current: LayoutContext;
  private scheduledFrame: number | undefined;
  private stopDprWatcher: (() => void) | undefined;
  private started = false;
  private readonly onResize = (): void => this.scheduleRefreshFromWindow();
  private readonly runScheduledRefresh = (): void => {
    this.scheduledFrame = undefined;
    this.refreshFromWindow();
  };

  constructor(
    private readonly eventBus: EventBus,
    options: LayoutServiceOptions = {},
  ) {
    const safeAreaProvider = options.safeAreaProvider ?? (() => ({ ...ZERO_SAFE_AREA }));
    this.viewportProbe = options.viewportProbe ?? new BrowserViewportProbe(safeAreaProvider);
    this.current = this.compute({
      width: 1,
      height: 1,
      devicePixelRatio: 1,
      safeArea: safeAreaProvider(),
    });
  }

  /** Attach window listeners and publish the first context. */
  start(): void {
    if (!this.started && typeof window !== 'undefined') {
      this.started = true;
      window.addEventListener('resize', this.onResize);
      window.screen.orientation?.addEventListener('change', this.onResize);
      window.visualViewport?.addEventListener('resize', this.onResize, { passive: true });
      window.visualViewport?.addEventListener('scroll', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize);
      this.stopDprWatcher = this.watchDevicePixelRatio();
      this.refreshFromWindow();
    }
  }

  stop(): void {
    if (this.started && typeof window !== 'undefined') {
      this.started = false;
      window.removeEventListener('resize', this.onResize);
      window.screen.orientation?.removeEventListener('change', this.onResize);
      window.visualViewport?.removeEventListener('resize', this.onResize);
      window.visualViewport?.removeEventListener('scroll', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);
      this.stopDprWatcher?.();
      this.stopDprWatcher = undefined;
      if (this.scheduledFrame !== undefined) {
        window.cancelAnimationFrame(this.scheduledFrame);
        this.scheduledFrame = undefined;
      }
    }
  }

  get context(): LayoutContext {
    return this.current;
  }

  /** Direct control-flow subscription. EventBus remains notifications-only. */
  subscribe(listener: LayoutListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Unit-testable measurement entry point. Publishes the next context to direct subscribers.
   */
  update(
    width: number,
    height: number,
    devicePixelRatio: number,
    safeArea: SafeArea = ZERO_SAFE_AREA,
  ): LayoutContext {
    return this.publish({ width, height, devicePixelRatio, safeArea });
  }

  private refreshFromWindow(): void {
    this.publish(this.viewportProbe.measure());
  }

  private publish(measured: ViewportMeasurement): LayoutContext {
    const previousOrientation = this.current.orientation;
    const next = this.compute(measured);
    this.current = next;
    for (const listener of [...this.listeners]) listener(next);
    if (next.orientation !== previousOrientation) {
      this.eventBus.emit('orientation:changed', next.orientation);
    }
    return next;
  }

  private scheduleRefreshFromWindow(): void {
    if (this.scheduledFrame !== undefined || typeof window === 'undefined') return;
    this.scheduledFrame = window.requestAnimationFrame(this.runScheduledRefresh);
  }

  private watchDevicePixelRatio(): () => void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return () => undefined;
    }

    let query: MediaQueryList | undefined;
    const remove = (): void => {
      query?.removeEventListener('change', onChange);
    };
    const updateQuery = (): void => {
      remove();
      query = window.matchMedia(`(resolution: ${window.devicePixelRatio || 1}dppx)`);
      query.addEventListener('change', onChange);
    };
    const onChange = (): void => {
      updateQuery();
      this.scheduleRefreshFromWindow();
    };

    updateQuery();
    return remove;
  }

  private compute(measurement: ViewportMeasurement): LayoutContext {
    const width = positiveFinite(measurement.width, 1);
    const height = positiveFinite(measurement.height, 1);
    const safeArea = sanitizeSafeArea(measurement.safeArea, width, height);
    const safeBounds = boundsFor(width, height, safeArea);
    const orientation = orientationForSize(safeBounds.width, safeBounds.height);
    return {
      width,
      height,
      orientation,
      safeArea,
      safeBounds,
      widthClass: widthClassFor(safeBounds.width),
      heightClass: heightClassFor(safeBounds.height),
      devicePixelRatio: positiveFinite(measurement.devicePixelRatio, 1),
    };
  }
}

function widthClassFor(width: number): WidthClass {
  if (width < 600) return 'compact';
  if (width < 840) return 'medium';
  if (width < 1200) return 'expanded';
  return 'large';
}

function heightClassFor(height: number): HeightClass {
  if (height < 480) return 'compact';
  if (height < 900) return 'medium';
  return 'expanded';
}

function sanitizeSafeArea(safeArea: SafeArea, width: number, height: number): SafeArea {
  const top = clampInset(safeArea.top, height);
  const right = clampInset(safeArea.right, width);
  const bottom = clampInset(safeArea.bottom, Math.max(0, height - top));
  const left = clampInset(safeArea.left, Math.max(0, width - right));
  return { top, right, bottom, left };
}

function boundsFor(width: number, height: number, safeArea: SafeArea): LayoutBounds {
  const safeWidth = Math.max(0, width - safeArea.left - safeArea.right);
  const safeHeight = Math.max(0, height - safeArea.top - safeArea.bottom);
  return {
    x: safeArea.left,
    y: safeArea.top,
    width: safeWidth,
    height: safeHeight,
    left: safeArea.left,
    right: safeArea.left + safeWidth,
    top: safeArea.top,
    bottom: safeArea.top + safeHeight,
  };
}

function clampInset(value: number, maximum: number): number {
  return Math.min(maximum, Math.max(0, Number.isFinite(value) ? value : 0));
}

function positiveFinite(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}
